// contexts/ParentContext.js

import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllParents,
  getParentById,
  createParent,
  updateParent,
  deleteParent
} from '../services/parentService';
import Notification from '../components/Common/Notification';

export const ParentContext = createContext();

export const ParentProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [parents, setParents] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    if (!user && !storedUser) {
      console.error('User or token not available');
      return {};
    }

    const token = user?.token || JSON.parse(storedUser).token;
    return { Authorization: `Bearer ${token}` };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllParents = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllParents(authHeaders);
      setParents(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching parents.';
      showNotification(errorMessage?.errorMessage, true);
    }
  };

  const fetchParentById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getParentById(id, authHeaders);
      setSelectedParent(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching parent with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addParent = async (parentData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newParent = await createParent(parentData, authHeaders);
      setParents(prevParents => [...prevParents, newParent]);
      showNotification('Parent added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding parent.';
      showNotification(errorMessage, true);
    }
  };

  const editParent = async (id, parentData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedParent = await updateParent(id, parentData, authHeaders);
      setParents(prevParents =>
        prevParents.map(parent => (parent.id === id ? updatedParent : parent))
      );
      showNotification('Parent updated successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating parent.';
      showNotification(errorMessage, true);
    }
  };

  const removeParent = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteParent(id, authHeaders);
      setParents(prevParents => prevParents.filter(parent => parent.id !== id));
      showNotification('Parent removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing parent.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllParents();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <ParentContext.Provider
      value={{
        parents,
        selectedParent,
        fetchAllParents,
        fetchParentById,
        addParent,
        editParent,
        removeParent,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </ParentContext.Provider>
  );
};
